import React, { createContext, useContext, useState, useCallback } from 'react';
import { Audio } from 'expo-av';
import { audioRecordingService } from './audioRecordingService';

interface AudioContextType {
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string>;
  hasPermission: boolean;
  requestPermission: () => Promise<boolean>;
  playAudio: (url: string, timestamp?: number) => Promise<void>;
  stopAudio: () => Promise<void>;
  currentSound: Audio.Sound | null;
  isPlaying: boolean;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [currentSound, setCurrentSound] = useState<Audio.Sound | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const requestPermission = useCallback(async () => {
    const granted = await audioRecordingService.requestPermissions();
    setHasPermission(granted);
    return granted;
  }, []);

  const startRecording = useCallback(async () => {
    try {
      await audioRecordingService.startRecording();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      throw error;
    }
  }, []);

  const stopRecording = useCallback(async () => {
    try {
      const uri = await audioRecordingService.stopRecording();
      setIsRecording(false);
      return uri;
    } catch (error) {
      console.error('Error stopping recording:', error);
      setIsRecording(false);
      throw error;
    }
  }, []);

  const playAudio = useCallback(async (url: string, timestamp?: number) => {
    try {
      if (!url) {
        throw new Error('Audio URL is required');
      }

      // Stop any currently playing audio
      if (currentSound) {
        await currentSound.stopAsync();
        await currentSound.unloadAsync();
        setCurrentSound(null);
        setIsPlaying(false);
      }

      const { sound } = await Audio.Sound.createAsync(
        { uri: url },
        { shouldPlay: true }
      );
      
      // Add status update listener
      sound.setOnPlaybackStatusUpdate((status) => {
        if (status.isLoaded) {
          setIsPlaying(status.isPlaying);
          if (!status.isPlaying && status.didJustFinish) {
            sound.unloadAsync();
            setCurrentSound(null);
          }
        }
      });
      
      setCurrentSound(sound);
      setIsPlaying(true);
      await sound.playAsync();
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
      throw error;
    }
  }, [currentSound]);

  const stopAudio = useCallback(async () => {
    try {
      if (currentSound) {
        await currentSound.stopAsync();
        await currentSound.unloadAsync();
        setCurrentSound(null);
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('Error stopping audio:', error);
      setIsPlaying(false);
      throw error;
    }
  }, [currentSound]);

  return (
    <AudioContext.Provider
      value={{
        isRecording,
        startRecording,
        stopRecording,
        hasPermission,
        requestPermission,
        playAudio,
        stopAudio,
        currentSound,
        isPlaying
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};
